import { MeiliSearch } from 'meilisearch';
import { captureException } from '@sentry/sveltekit';
import { MEILISEARCH_API_KEY, MEILISEARCH_API_HOST } from '../constants';
import { PUBLIC_METAFY_ENV } from '$env/static/public';
import { debouncePromise } from './functions';
import { getApolloClient, gql } from '$lib/apollo';

let client: any;
let clientIndexKey: any;

/**
 * Searches the intended index.
 *
 */
export const searchIndex = debouncePromise(async (index: string | any[], query = '', options = {}) => {
  try {
    if (!index) {
      throw new Error('Must provide an index');
    }
    return searchWithMeili(index, query, options);
  } catch (error) {
    captureException(error);
    return {};
  }
}, 150);

export async function searchWithMeili(index: string | any[], query = '', options = {}) {
  try {
    if (!client) {
      client = new MeiliSearch({
        host: MEILISEARCH_API_HOST,
        apiKey: MEILISEARCH_API_KEY
      });
    }

    // Determine which environment to query
    let prefix = '';
    if (PUBLIC_METAFY_ENV === 'development') {
      prefix = 'dev';
    } else if (PUBLIC_METAFY_ENV === 'staging') {
      prefix = 'stag';
    } else if (PUBLIC_METAFY_ENV === 'production') {
      prefix = 'prod';
    }

    // Handle multiple index search
    if (Array.isArray(index)) {
      const prefixed_indexes = index.map(i => ({
        indexUid: `${prefix}_${i.indexName}`,
        q: query,
        ...i.params
      }));
      return await client.multiSearch({ queries: prefixed_indexes });
    } else {
      const prefixed_index = `${prefix}_${index}`;
      return await client.index(prefixed_index).search(query, options);
    }
  } catch (error) {
    captureException(error);
    return {};
  }
}

export async function searchKeyWithMeili(query = '', options = {}) {
  try {
    if (!clientIndexKey) {
      const res = await getApolloClient().mutate({
        mutation: gql`
          mutation SearchKey($input: SearchKeyInput!) {
            searchKey(input: $input) {
              result
              errors {
                ... on Error {
                  message
                  __typename
                }
              }
            }
          }
        `,
        variables: {
          input: {
            index: 'PRODUCTS'
          }
        }
      });

      clientIndexKey = new MeiliSearch({
        host: MEILISEARCH_API_HOST,
        apiKey: res.data.searchKey.result
      });
    }

    // Determine which environment to query
    let prefix = '';
    if (PUBLIC_METAFY_ENV === 'development') {
      prefix = 'dev';
    } else if (PUBLIC_METAFY_ENV === 'staging') {
      prefix = 'stag';
    } else if (PUBLIC_METAFY_ENV === 'production') {
      prefix = 'prod';
    }

    return await clientIndexKey.index(`${prefix}_products`).search(query, options);
  } catch (error) {
    captureException(error);
    return {};
  }
}

interface SearchObject {
  index: string;
  page: number;
  filters: any;
  hitsPerPage: number;
}

/**
 * Searches the intended index.
 */
export const doSearch = async ({ index, page = 1, filters = {}, hitsPerPage = 9 }: SearchObject) => {
  const filtersApplied = Object.values(filters)
    .filter(f => f !== '')
    .join(' AND ');

  try {
    const data = await searchIndex(index, '', {
      filter: filtersApplied,
      hitsPerPage,
      page
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Map the coach object returned by Search to match our format
 */
export function mapSearchCoachToCoach(acoach: any) {
  const {
    id,
    slug,
    name,
    title,
    summary,
    avatar,
    games = [],
    achievements = [],
    nextAvailability,
    minLivePriceCents,
    minNonZeroLivePriceCents,
    offersFreeLiveLesson,
    minReplayPriceCents,
    minNonZeroReplayPriceCents,
    offersFreeReplayLesson,
    topMentor = []
  } = acoach;
  return {
    id,
    slug,
    name,
    title,
    summary,
    account: {
      avatar,
      name
    },
    coachAchievements: achievements,
    nextAvailability,
    minLivePriceCents,
    minNonZeroLivePriceCents,
    offersFreeLiveLesson,
    minReplayPriceCents,
    minNonZeroReplayPriceCents,
    offersFreeReplayLesson,
    topMentor,
    coachGames: games.map((game: any) => ({
      game,
      minLivePriceCents,
      minNonZeroLivePriceCents,
      offersFreeLiveLesson,
      minReplayPriceCents,
      minNonZeroReplayPriceCents,
      offersFreeReplayLesson
    }))
  };
}
